import React, { useMemo, useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { useParams } from "react-router-dom";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { useEmployeeData } from "../context/EmployeeDataContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus, FaUmbrellaBeach, FaPaperPlane, FaFilePdf, FaChevronRight, FaTimes, FaTable
} from "react-icons/fa";
import { format, parseISO, getMonth, getYear } from "date-fns";

export default function EmployeeLeaves() {
  const { user } = useEmployeeAuth();
  const { email } = useParams();
  const targetEmail = email || user.email;
  const { leaves, employee } = useEmployeeData();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showUsedModal, setShowUsedModal] = useState(null);
  const [showMonthlyTable, setShowMonthlyTable] = useState(true);

  const [form, setForm] = useState({
    fromDate: "", toDate: "", type: "casual", duration: "full", reason: ""
  });
  const salarySource =
    user.role === "hr" && targetEmail !== user.email
      ? selectedEmployee
      : employee;

  const monthlySalary = salarySource?.salaryStructure?.gross || 0;
  const perDayCut = monthlySalary / 30;
  console.log("SALARY:", monthlySalary);
  console.log("PER DAY:", perDayCut);
  console.log("FULL EMPLOYEE:", employee);


  const years = useMemo(() => {
    const list = [new Date().getFullYear()];
    leaves.forEach(l => {
      const y = getYear(new Date(l.fromDate));
      if (!list.includes(y)) list.push(y);
    });
    return list.sort((a, b) => b - a);
  }, [leaves]);

  const metrics = useMemo(() => {
    // ALL leaves for the year — for display (includes pending & rejected)
    const allYearLeaves = leaves.filter(l => {
      console.log("CHECK:", l.employeeEmail, targetEmail);
      return (
        l.employeeEmail === targetEmail &&
        getYear(new Date(l.fromDate)) === selectedYear
      );
    });

    // Only APPROVED non-bonus leaves for calculations
    const approvedLeaves = allYearLeaves.filter(l =>
      l.status === "approved" && l.type !== "bonus"
    );

    const casualLeaves = approvedLeaves
      .filter(l => l.type === "casual")
      .sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));

    const sickLeaves = approvedLeaves.filter(l => l.type === "sick");

    // Sick Leave Logic: 6 fixed/year, 3 halfs = 1 full
    const sickFull = sickLeaves.filter(l => l.duration === "full" || !l.duration).reduce((s, l) => s + (l.days || 1), 0);
    const sickHalf = sickLeaves.filter(l => l.duration === "half").length;
    const sickUsed = sickFull + Math.floor(sickHalf / 3);
    const sickLeft = 6 - sickUsed;
    const sickPendingHalf = sickHalf % 3;

    // ======================================================
    // CASUAL LEAVE POLICY ENGINE
    //
    // Rules:
    //  - 12 free/paid days per year
    //  - Each month gives 1 free day from the pool (cumulative)
    //  - At most 1 FREE leave per calendar month — extra = "Deducted from pay"
    //  - "Deducted from pay" does NOT consume the pool counter
    //  - 3 half-days = 1 full day; all 3 get the same status when triplet completes
    //  - Remaining half-days (< 3) = "Carrying to next"
    // ======================================================
    const casualStatusMap = {};            // leaveId -> paymentStatus string
    const monthlyBreakdown = {};            // month# -> { total, free, deducted, carried }
    const monthlyFreeUsed = {};            // month# -> true/false
    let poolUsed = 0;             // free days consumed from the 12-day annual pool
    let halfDayBank = 0;
    let currentTriplet = [];            // ids awaiting triplet completion
    let pendingHalfCount = 0;

    const getMonth_ = (m) => {
      if (!monthlyBreakdown[m]) monthlyBreakdown[m] = { total: 0, free: 0, deducted: 0, carried: 0 };
      return monthlyBreakdown[m];
    };

    for (const cl of casualLeaves) {
      const leaveMonth = getMonth(new Date(cl.fromDate)) + 1;
      const cumulBudget = leaveMonth; // cumulative budget: 1 free/month
      const mb = getMonth_(leaveMonth);

      if (cl.duration === "half") {
        halfDayBank++;
        pendingHalfCount++;
        currentTriplet.push(cl.id);

        if (halfDayBank % 3 === 0) {
          // Triplet complete — resolve ALL 3 as 1 full day
          const freeAvailable = !monthlyFreeUsed[leaveMonth] && poolUsed < cumulBudget;
          const resolvedStatus = freeAvailable ? "Paid" : "Deducted from pay";

          if (freeAvailable) {
            poolUsed++;
            monthlyFreeUsed[leaveMonth] = true;
            mb.free += 1;
          } else {
            mb.deducted += 1;
          }
          mb.total += 1;
          pendingHalfCount = 0;

          for (const id of currentTriplet) casualStatusMap[id] = resolvedStatus;
          currentTriplet = [];
        } else {
          casualStatusMap[cl.id] = "Carrying to next";
          mb.total += 0.5;
          mb.carried += 0.5;
        }
      } else {
        // Full-day (possibly multi-day)
        const daysInLeave = cl.days || 1;
        let paidDays = 0, deductedDays = 0;
        for (let d = 0; d < daysInLeave; d++) {
          if (!monthlyFreeUsed[leaveMonth] && poolUsed < cumulBudget) {
            paidDays++;
            poolUsed++;
            monthlyFreeUsed[leaveMonth] = true;
          } else {
            deductedDays++;
          }
        }
        mb.total += daysInLeave;
        mb.free += paidDays;
        mb.deducted += deductedDays;

        let status = "Paid";
        if (paidDays === 0) status = "Deducted from pay";
        else if (deductedDays > 0) status = `${paidDays} Paid, ${deductedDays} Deducted`;
        casualStatusMap[cl.id] = status;
      }
    }

    // Add status to every leaf for display
    const processedLeaves = allYearLeaves.map(l => {
      if (l.status !== "approved")
        return { ...l, paymentStatus: l.status === "pending" ? "Pending Approval" : "Rejected" };
      if (l.type === "bonus") return { ...l, paymentStatus: "Bonus" };
      if (l.type === "sick") return { ...l, paymentStatus: "Paid" };
      if (l.type === "casual" && casualStatusMap[l.id] !== undefined)
        return { ...l, paymentStatus: casualStatusMap[l.id] };
      return l;
    });

    return {
      poolUsed,
      poolLeft: 12 - poolUsed,
      pendingHalfCount,
      sickUsed, sickLeft, sickTotal: 6, sickPendingHalf,
      yearLeaves: processedLeaves,
      casualList: processedLeaves.filter(l => l.type === "casual" && l.status === "approved"),
      sickList: processedLeaves.filter(l => l.type === "sick" && l.status === "approved"),
      monthlyBreakdown,
    };
  }, [leaves, selectedYear]);

  const groupedLeaves = useMemo(() => {
    const groups = {};
    metrics.yearLeaves.forEach(l => {
      const month = format(new Date(l.fromDate), "MMMM");
      if (!groups[month]) groups[month] = [];
      groups[month].push(l);
    });
    return groups;
  }, [metrics.yearLeaves]);

  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fromDate || !form.toDate || !form.reason) return alert("Fill all fields");
    let days = form.duration === "half" ? 0.5
      : Math.round((new Date(form.toDate) - new Date(form.fromDate)) / 86400000) + 1;
    if (days <= 0) return alert("Invalid dates");
    try {
      await addDoc(collection(db, "leaves"), {
        employeeEmail: targetEmail,
        employeeName: selectedEmployee?.name || (targetEmail === user.email ? user.name : targetEmail.split("@")[0]),
        fromDate: form.fromDate, toDate: form.toDate,
        type: form.type, duration: form.duration,
        reason: form.reason, days,
        status: "pending",
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setForm({ fromDate: "", toDate: "", type: "casual", duration: "full", reason: "" });
      setShowForm(false);
      alert("Leave applied successfully!");
    } catch { alert("Submission failed"); }
  };

  const generatePDF = (leave) => {
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><body style="font-family:serif;padding:40px">
      <h1 style="text-align:center;color:#1e40af">DriveEasy Leave</h1>
      <p><b>Employee:</b> ${leave.employeeName}</p>
      <p><b>Period:</b> ${leave.fromDate} → ${leave.toDate}</p>
      <p><b>Type:</b> ${leave.type?.toUpperCase()}</p>
      <p><b>Duration:</b> ${leave.duration === "half" ? "Half Day" : "Full Day"}</p>
      <p><b>Reason:</b> ${leave.reason}</p>
      <p><b>Status:</b> ${leave.status?.toUpperCase()}</p>
      <hr/><p style="font-size:11px;color:gray">Generated ${new Date().toLocaleString()}</p>
    </body></html>`);
    w.print();
  };

  useEffect(() => {
    if (!targetEmail) return;

    const q = query(
      collection(db, "employees"),
      where("email", "==", targetEmail)
    );

    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        setSelectedEmployee(snap.docs[0].data());
      }
    });

    return () => unsub();
  }, [targetEmail]);

  useEffect(() => {
    if (!metrics?.monthlyBreakdown) return;

    const saveSummary = async () => {
      try {
        const summary = {};

        Object.entries(metrics.monthlyBreakdown).forEach(([month, data]) => {
          const perDay = monthlySalary / 30;
          summary[month] = {
            days: data.deducted,
            amount: Math.round(data.deducted * perDay)
          };
        });

        await updateDoc(
          doc(db, "employees", targetEmail),
          {
            [`leaveSummary.${selectedYear}`]: summary,
            updatedAt: new Date()
          }
        );

      } catch (err) {
        console.error("Failed to save summary:", err);
      }
    };

    saveSummary();
  }, [metrics.monthlyBreakdown, targetEmail, selectedYear, monthlySalary]);

  const statusColor = (ps) =>
    ps === "Paid" ? "text-emerald-400/70" :
      ps === "Bonus" ? "text-yellow-400/70" :
        ps === "Carrying to next" ? "text-amber-400/70" :
          ps === "Pending Approval" ? "text-blue-400/70" :
            ps === "Rejected" ? "text-rose-400/30 line-through" :
              "text-rose-400/70";

  const statusBadge = (ps) =>
    ps === "Paid" ? "bg-emerald-500/10 text-emerald-400" :
      ps === "Carrying to next" ? "bg-amber-500/10 text-amber-400" :
        "bg-rose-500/10 text-rose-400";

  return (
    <div className="min-h-screen bg-neutral-950 p-6 md:p-10 text-white">

      {/* Header */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
        <div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-white to-white/40 bg-clip-text text-transparent">MY LEAVES</h1>
          <p className="text-white/40 text-sm mt-1">Official leave tracking & balance system.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-white font-bold outline-none">
            {years.map(y => <option key={y} value={y} className="bg-neutral-900">{y}</option>)}
          </select>
          <button onClick={() => setShowMonthlyTable(v => !v)}
            className="bg-white/5 border border-white/10 px-5 py-3 rounded-2xl font-bold hover:bg-white/10 transition flex items-center gap-2 text-white/60">
            <FaTable /> Monthly View
          </button>
          <button onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-7 py-3 rounded-2xl font-bold hover:scale-105 transition shadow-xl flex items-center gap-2">
            <FaPlus /> Apply
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Casual Leaves */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-[40px] p-8 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition text-6xl">🌴</div>
          <h3 className="text-cyan-400 font-black uppercase tracking-widest text-xs mb-5">Casual Leaves (CL)</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-white/40 text-[10px] font-bold uppercase">Annual Allowance</p>
              <p className="text-4xl font-black">12 days</p>
            </div>
            <div className="cursor-pointer group/stat" onClick={() => setShowUsedModal("casual")}>
              <p className="text-white/40 text-[10px] font-bold uppercase group-hover/stat:text-cyan-400 transition">Free Used</p>
              <p className="text-4xl font-black text-rose-400">{metrics.poolUsed} days</p>
              {metrics.pendingHalfCount > 0 &&
                <p className="text-[10px] text-amber-400 mt-1">+{metrics.pendingHalfCount} half-days carrying</p>}
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 flex justify-between text-[10px] text-white/30 font-bold uppercase tracking-widest">
            <span>Remaining: {metrics.poolLeft} days</span>
            <span>1 Free per month · Extras deducted</span>
          </div>
        </motion.div>

        {/* Sick Leaves */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-[40px] p-8 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition text-6xl">🏥</div>
          <h3 className="text-purple-400 font-black uppercase tracking-widest text-xs mb-5">Sick Leaves (SL)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-white/40 text-[10px] font-bold uppercase">Allowance</p>
              <p className="text-3xl font-black">{metrics.sickTotal} days</p>
            </div>
            <div className="cursor-pointer group/stat" onClick={() => setShowUsedModal("sick")}>
              <p className="text-white/40 text-[10px] font-bold uppercase group-hover/stat:text-purple-400 transition">Used</p>
              <p className="text-3xl font-black text-rose-400">{metrics.sickUsed} days</p>
              {metrics.sickPendingHalf > 0 && <p className="text-[10px] text-white/20">+{metrics.sickPendingHalf} half</p>}
            </div>
            <div>
              <p className="text-white/40 text-[10px] font-bold uppercase">Balance</p>
              <p className="text-3xl font-black text-emerald-400">{metrics.sickLeft} days</p>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 flex justify-between text-[10px] text-white/30 font-bold uppercase tracking-widest">
            <span>Fixed: 6 Days</span><span>3 Half-days = 1 full</span>
          </div>
        </motion.div>
      </div>

      {/* Monthly Breakdown Table */}
      <AnimatePresence>
        {showMonthlyTable && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="max-w-7xl mx-auto mb-10 bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
            <div className="px-8 py-5 border-b border-white/10 flex justify-between items-center">
              <h4 className="font-black text-white uppercase tracking-widest text-xs">📅 {selectedYear} Monthly Breakdown</h4>
              <button onClick={() => setShowMonthlyTable(false)} className="text-white/30 hover:text-white"><FaTimes /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="text-white/30 font-black uppercase tracking-widest border-b border-white/5">
                    <th className="px-6 py-3">Month</th>
                    <th className="px-6 py-3">Total Taken</th>
                    <th className="px-6 py-3 text-emerald-400">Free (Paid)</th>
                    <th className="px-6 py-3 text-rose-400">Deducted from Pay</th>
                    <th className="px-6 py-3 text-red-300">Deducted (₹)</th>
                    <th className="px-6 py-3 text-amber-400">Carried to Next</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {Object.entries(metrics.monthlyBreakdown)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([m, data]) => (
                      <tr key={m} className="hover:bg-white/5 transition">
                        <td className="px-6 py-3 font-bold text-white">{MONTH_NAMES[Number(m) - 1]}</td>
                        <td className="px-6 py-3 font-bold">{data.total} {data.total === 1 ? "day" : "days"}</td>
                        <td className="px-6 py-3 font-black text-emerald-400">{data.free > 0 ? `${data.free} day` : "—"}</td>
                        <td className="px-6 py-3 font-black text-rose-400">{data.deducted > 0 ? `${data.deducted} day` : "—"}</td>
                        <td className="px-6 py-3 font-black text-red-300">
                          {data.deducted > 0 ? (
                            <>
                              ₹{Math.round(data.deducted * perDayCut).toLocaleString("en-IN")}
                              <div className="text-[9px] text-white/30 font-bold">
                                (₹{Math.round(perDayCut).toLocaleString("en-IN")}/day)
                              </div>
                            </>
                          ) : "—"}
                        </td>
                        <td className="px-6 py-3 font-black text-amber-400">{data.carried > 0 ? `${data.carried} day` : "—"}</td>
                      </tr>
                    ))}
                  {Object.keys(metrics.monthlyBreakdown).length === 0 &&
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-white/20 font-bold">No casual leaves taken in {selectedYear}</td></tr>}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leave Cards */}
      <div className="max-w-7xl mx-auto space-y-10">
        {Object.keys(groupedLeaves).length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-[40px] border border-white/10">
            <FaUmbrellaBeach className="mx-auto text-8xl text-white/10 mb-6" />
            <p className="text-white/40 text-xl font-bold">No history for {selectedYear}</p>
          </div>
        ) : (
          Object.keys(groupedLeaves).reverse().map(month => (
            <div key={month}>
              <div className="flex items-center gap-4 mb-5">
                <h4 className="text-lg font-black text-white">{month}</h4>
                <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {groupedLeaves[month].map(l => (
                  <motion.div key={l.id} whileHover={{ y: -4 }} onClick={() => setSelectedLeave(l)}
                    className="group cursor-pointer bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/8 transition shadow-lg relative">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className={`text-[10px] font-black uppercase tracking-widest ${l.type === "casual" ? "text-cyan-400" : l.type === "sick" ? "text-purple-400" :
                          l.type === "bonus" ? "text-yellow-400" : "text-blue-400"}`}>
                          {l.type} • {l.duration === "half" ? "half" : "full"}
                        </span>
                        <h5 className="font-black text-white text-lg mt-0.5">{format(parseISO(l.fromDate), "do MMM")}</h5>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase ${l.status === "approved" ? "bg-emerald-500/20 text-emerald-400" :
                        l.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-rose-500/20 text-rose-400"}`}>
                        {l.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className={`text-[11px] font-bold uppercase tracking-wide ${statusColor(l.paymentStatus)}`}>
                        {l.paymentStatus || "Processing"}
                      </p>
                      {l.status === "approved" && (
                        <div className="flex gap-1.5">
                          <span className="bg-white/10 px-2 py-0.5 rounded text-[8px] font-black uppercase text-cyan-400">{l.type}</span>
                          <span className="bg-white/10 px-2 py-0.5 rounded text-[8px] font-black uppercase text-white/30">{l.duration || "full"}</span>
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition text-cyan-400">
                      <FaChevronRight size={12} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* History Modal */}
      <AnimatePresence>
        {showUsedModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowUsedModal(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-neutral-900 border border-white/10 rounded-[36px] p-7 w-full max-w-sm shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="text-lg font-black text-white uppercase">
                    {showUsedModal === "casual" ? "🌴 Casual" : "🏥 Sick"} History
                  </h3>
                  <p className="text-[10px] text-white/30 font-bold mt-0.5">Tap outside to close</p>
                </div>
                <button onClick={() => setShowUsedModal(null)}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition">
                  <FaTimes size={12} />
                </button>
              </div>
              <div className="max-h-[360px] overflow-y-auto custom-scrollbar space-y-2 pr-1">
                {(showUsedModal === "casual" ? metrics.casualList : metrics.sickList).length === 0
                  ? <p className="text-center text-white/20 py-10 font-bold">No records yet.</p>
                  : (showUsedModal === "casual" ? metrics.casualList : metrics.sickList)
                    .sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate))
                    .map((l, i) => (
                      <div key={l.id || i} className="flex items-center justify-between px-4 py-3 bg-white/5 rounded-2xl border border-white/5">
                        <div>
                          <p className="text-white text-sm font-bold">{format(parseISO(l.fromDate), "dd MMM yyyy")}</p>
                          <p className="text-[10px] text-white/30 uppercase font-bold mt-0.5">
                            {l.duration === "half" ? "Half Day" : l.days > 1 ? `${l.days} Days` : "Full Day"}
                          </p>
                        </div>
                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-full ${statusBadge(l.paymentStatus)}`}>
                          {l.paymentStatus}
                        </span>
                      </div>
                    ))
                }
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Apply Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowForm(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-neutral-950 border border-white/20 rounded-[40px] p-10 w-full max-w-2xl shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <h3 className="text-3xl font-black text-white mb-8 text-center uppercase tracking-tighter">Apply for Leave</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/40 uppercase ml-2 tracking-widest">From</label>
                    <input type="date" required value={form.fromDate}
                      onChange={e => setForm(f => ({ ...f, fromDate: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-cyan-400" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/40 uppercase ml-2 tracking-widest">To</label>
                    <input type="date" required value={form.toDate}
                      onChange={e => setForm(f => ({ ...f, toDate: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-cyan-400" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-bold appearance-none">
                    <option value="casual" className="bg-neutral-900">Casual Leave</option>
                    <option value="sick" className="bg-neutral-900">Sick Leave</option>
                    <option value="earned" className="bg-neutral-900">Earned Leave</option>
                    <option value="bonus" className="bg-neutral-900">Bonus Leave (Reward)</option>
                  </select>
                  <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-bold appearance-none">
                    <option value="full" className="bg-neutral-900">Full Day</option>
                    <option value="half" className="bg-neutral-900">Half Day</option>
                  </select>
                </div>
                <textarea required rows={4} value={form.reason}
                  onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-[30px] px-6 py-6 text-white placeholder-white/20 resize-none outline-none focus:ring-2 focus:ring-cyan-400"
                  placeholder="Reason for leave..." />
                <div className="flex gap-4 justify-end">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-10 py-4 bg-white/5 hover:bg-white/10 rounded-2xl text-white/40 font-black uppercase tracking-widest text-xs transition">
                    Cancel
                  </button>
                  <button type="submit"
                    className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl text-white font-black uppercase tracking-widest text-xs shadow-xl hover:scale-105 transition flex items-center gap-2">
                    <FaPaperPlane /> Request Approval
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Details Modal */}
      <AnimatePresence>
        {selectedLeave && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedLeave(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-neutral-900 border border-white/20 rounded-[40px] p-10 w-full max-w-2xl shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-8">
                <div>
                  <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">
                    {selectedLeave.type} • {selectedLeave.duration || "full"}
                  </span>
                  <h3 className="text-3xl font-black text-white leading-tight mt-1">Leave Details</h3>
                </div>
                <button onClick={() => setSelectedLeave(null)} className="text-white/40 hover:text-white transition"><FaTimes size={22} /></button>
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-5">
                  <div className="bg-white/5 p-5 rounded-3xl border border-white/5">
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-1">Period</p>
                    <p className="text-white font-bold text-sm">
                      {format(parseISO(selectedLeave.fromDate), "MMM do")} → {format(parseISO(selectedLeave.toDate), "MMM do, yyyy")}
                    </p>
                  </div>
                  <div className="bg-white/5 p-5 rounded-3xl border border-white/5">
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-1">Status</p>
                    <p className={`font-bold uppercase text-sm ${selectedLeave.status === "approved" ? "text-emerald-400" :
                      selectedLeave.status === "pending" ? "text-yellow-400" : "text-rose-400"}`}>
                      {selectedLeave.status}
                    </p>
                  </div>
                </div>
                {selectedLeave.paymentStatus && (
                  <div className="bg-white/5 p-5 rounded-3xl border border-white/5">
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-1">Payment Status</p>
                    <p className={`font-bold text-sm ${statusColor(selectedLeave.paymentStatus)}`}>{selectedLeave.paymentStatus}</p>
                  </div>
                )}
                <div className="bg-white/5 p-6 rounded-3xl border border-white/5">
                  <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-2">Reason</p>
                  <p className="text-white text-base italic">"{selectedLeave.reason}"</p>
                </div>
              </div>
              <div className="mt-8 flex gap-4">
                <button onClick={() => generatePDF(selectedLeave)}
                  className="flex-1 py-4 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-2xl font-black uppercase text-xs tracking-widest transition flex items-center justify-center gap-2">
                  <FaFilePdf /> Export
                </button>
                <button onClick={() => setSelectedLeave(null)}
                  className="flex-1 py-4 bg-white/5 hover:bg-white/10 text-white/40 rounded-2xl font-black uppercase text-xs tracking-widest transition">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius:10px; }
      `}</style>
    </div>
  );
}
